import { DataType } from "@totalenergiescode/mobility-business-rev-form-data/lib";
import type { DecoratorBuilder } from "@totalenergiescode/mobility-business-rev-form-data/store";

import CheckboxWithLabel from "./CheckboxWithLabel/CheckboxWithLabel.tsx";
import DatePickerField from "./DatePickerField/DatePickerField.tsx";
import DateRangePickerField from "./DateRangePickerField/DateRangePickerField.tsx";
import DividedInput from "./DividedInput/DividedInput.tsx";
import EditableCard from "./EditableCard/EditableCard.tsx";
import InputWithUnit from "./InputWithUnit/InputWithUnit.tsx";
import { MultiselectDays } from "./MultiselectDays/MultiselectDays.tsx";
import RadioGroup from "./RadioGroup/RadioGroup.tsx";
import SimpleInput from "./SimpleInput/SimpleInput.tsx";
import SimpleSelect from "./SimpleSelect/SimpleSelect.tsx";
import SubStructureSelector from "./SubStructureSelector/SubStructureSelector.tsx";
import DefaultSummaryDecorator from "./Summary/DefaultSummaryDecorator.tsx";
import EditableCardSummary from "./Summary/EditableCardSummary.tsx";
import TimePickerField from "./TimePickerField/TimePickerField.tsx";
import TimeRangePickerField from "./TimeRangePickerField/TimeRangePickerField.tsx";
import ToggleSwitch from "./ToggleSwitch/ToggleSwitch.tsx";

const commonDecorators: DecoratorBuilder[] = [
  {
    code: "SIMPLE_INPUT",
    type: DataType.STRING,
    input: (props) => <SimpleInput fieldId={props.fieldId} />,
  },
  {
    code: "SIMPLE_SELECT",
    type: DataType.SELECTION,
    input: (props) => <SimpleSelect fieldId={props.fieldId} />,
  },
  {
    code: "CHECKBOX_WITH_LABEL",
    type: DataType.BOOLEAN,
    input: (props) => <CheckboxWithLabel fieldId={props.fieldId} />,
  },
  {
    code: "RADIO_GROUP",
    type: DataType.SELECTION,
    input: (props) => <RadioGroup fieldId={props.fieldId} />,
  },
  {
    code: "TOGGLE",
    type: DataType.BOOLEAN,
    input: (props) => <ToggleSwitch fieldId={props.fieldId} />,
  },
  {
    code: "DIVIDED_INPUT",
    type: DataType.STRING,
    input: (props) => <DividedInput fieldId={props.fieldId} />,
  },
  {
    code: "TIME_PICKER",
    type: DataType.STRING,
    input: (props) => <TimePickerField fieldId={props.fieldId} />,
  },
  {
    code: "TIME_RANGE_PICKER",
    type: DataType.STRING,
    input: (props) => <TimeRangePickerField fieldId={props.fieldId} />,
  },
  {
    code: "DATE_PICKER",
    type: DataType.STRING,
    input: (props) => <DatePickerField fieldId={props.fieldId} />,
  },
  {
    code: "DATE_RANGE_PICKER",
    type: DataType.STRING,
    input: (props) => <DateRangePickerField fieldId={props.fieldId} />,
  },
  {
    code: "INPUT_WITH_UNIT",
    type: DataType.STRING,
    input: (props) => <InputWithUnit fieldId={props.fieldId} />,
  },
  {
    code: "SUB_STRUCTURE_SELECTOR",
    type: DataType.STRING,
    input: (props) => <SubStructureSelector fieldId={props.fieldId} />,
  },
  {
    code: "MULTISELECT_DAYS",
    type: DataType.STRING,
    input: (props) => (
      <MultiselectDays
        fieldId={props.fieldId}
        resetButtonTranslation='ledgers.order.timeControls.listOfDays.reset' // WARNING: certainly not the correct translation
        daysTranslation={[
          {
            trad: "ledgers.shared.days.monday",
            day: "monday",
          },
          {
            trad: "ledgers.shared.days.tuesday",
            day: "tuesday",
          },
          {
            trad: "ledgers.shared.days.wednesday",
            day: "wednesday",
          },
          {
            trad: "ledgers.shared.days.thursday",
            day: "thursday",
          },
          {
            trad: "ledgers.shared.days.friday",
            day: "friday",
          },
          {
            trad: "ledgers.shared.days.saturday",
            day: "saturday",
          },
          {
            trad: "ledgers.shared.days.sunday",
            day: "sunday",
          },
        ]}
      />
    ),
  },
  {
    code: "EDITABLE_CARD",
    type: DataType.STRING,
    input: (props) => (
      <EditableCard
        fieldId={props.fieldId}
        // WARNING: certainly not the correct translation keys
        titleAddressTranslation='formData.editableCard.address.title'
        titlePinTranslation='formData.editableCard.pin.title'
        cancelButtonTranslation='formData.editableCard.cancelButton'
        submitButtonTranslation='formData.editableCard.submitButton'
        nameLabelTranslation='formData.editableCard.nameLabel'
        addressLabelTranslation='formData.editableCard.addressLabel'
      />
    ),
    summary: (props) => (
      <EditableCardSummary
        fieldId={props.fieldId}
        nameLabelTranslation='formData.editableCard.nameLabel'
        addressLabelTranslation='formData.editableCard.addressLabel'
      />
    ),
  },
];

export const registerCommonDecorators = (): DecoratorBuilder[] =>
  commonDecorators.map((decorator) => ({
    summary: (props) => <DefaultSummaryDecorator fieldId={props.fieldId} />,
    ...decorator,
  }));
